import DecimalType from "decimal.js";
import { SerialType } from "@utils/types";

import { SubscriptionItemType, SubscriptionType } from "@/primary-models";
import { EXTRA_PRODUCT } from "@/constants/products";

import { TFreshMealPlan, TLitterPlan } from "./subscriptions.types";

export type TLitterPricingDetails = {
  unitPrice: DecimalType;
  totalPrice: DecimalType;
  bulkDiscountPercentage: DecimalType;
};

export enum LitterProduct {
  SCOOP_HEALTH = "SCOOP_HEALTH",
  SCOOP_PLANET = "SCOOP_PLANET",
}

export type TLitterProduct = keyof typeof LitterProduct;

export type TLitterPricingByQuantity = Record<number, TLitterPricingDetails>;

export interface IUpdateScoopFrequencyAndQuantityRequest {
  subscriptionId: number;
  frequencyInWeeks?: number;
  quantity?: number;
}
export interface IUpdateScoopFrequencyAndQuantityResponse {
  subscriptionId: number;
  frequencyInWeeks: number;
  quantity: number;
}

export const MAX_LITTER_QUANTITY = 4 as const;

export const MAX_LITTER_FREQUENCY_IN_WEEKS = 16 as const;

export function isLitterPlan(plan: SerialType<TFreshMealPlan | TLitterPlan>): plan is SerialType<TLitterPlan>;
export function isLitterPlan(plan: TFreshMealPlan | TLitterPlan): plan is TLitterPlan;
export function isLitterPlan(
  plan: SerialType<TFreshMealPlan | TLitterPlan> | TFreshMealPlan | TLitterPlan,
): plan is SerialType<TLitterPlan> | TLitterPlan {
  return plan.type === SubscriptionType.SCOOP_HEALTH
    || plan.type === SubscriptionType.SCOOP_PLANET;
}

export function isLitterPlanPlanet(
  plan: TFreshMealPlan | TLitterPlan,
): plan is TLitterPlan & {
  type: typeof SubscriptionType.SCOOP_PLANET
} {
  return plan.type === SubscriptionType.SCOOP_PLANET;
}

export function isLitterPlanHealth(
  plan: TFreshMealPlan | TLitterPlan,
): plan is TLitterPlan & {
  type: typeof SubscriptionType.SCOOP_HEALTH
} {
  return plan.type === SubscriptionType.SCOOP_HEALTH;
}

export const getPrettyLitterPlanName = (type: SubscriptionType): string => {
  if (type === SubscriptionType.SCOOP_HEALTH) {
    return EXTRA_PRODUCT.HEALTH_SCOOP_LITTER ?? "Scoop Health";
  }
  if (type === SubscriptionType.SCOOP_PLANET) {
    return EXTRA_PRODUCT.PLANET_SCOOP_LITTER ?? "Scoop Planet";
  }
  return "Scoop litter";
};

export const hasLitterPlanOfTypeNew = (subscriptions: TLitterPlan[] |
undefined, product: SubscriptionType) => {
  if (!subscriptions) {
    return false;
  }

  return subscriptions.find((plan) => plan.type === product);
};

// This is mostly to remind us to update the litter plan enum when we add a new product.
export const IS_LITTER_PRODUCT: Record<TLitterProduct, true>
& Record<Exclude<SubscriptionItemType, TLitterProduct>, false> = {
  [SubscriptionItemType.CLUCK]: false,
  [SubscriptionItemType.GOBBLE]: false,
  [SubscriptionItemType.MOOO]: false,
  [SubscriptionItemType.OINK]: false,
  [SubscriptionItemType.QUACK]: false,
  [SubscriptionItemType.SPLASH]: false,
  [SubscriptionItemType.BAAA]: false,
  [SubscriptionItemType.SCOOP_PLANET]: true,
  [SubscriptionItemType.SCOOP_HEALTH]: true,
  [SubscriptionItemType.NIBBLES_SALMON]: false,
  [SubscriptionItemType.NIBBLES_CHICKEN]: false,
  [SubscriptionItemType.SPRINKLES]: false,
  [SubscriptionItemType.TOTE_BAG]: false,
  [SubscriptionItemType.SHREDDED_CLUCK]: false,
  [SubscriptionItemType.SHREDDED_GOBBLE]: false,
};

export function isLitterProduct(product: string): product is TLitterProduct {
  return !!(IS_LITTER_PRODUCT as Partial<Record<string, boolean>>)[product];
}

export type TScoopPriceInfo = Record<TLitterProduct, Record<number, { price: DecimalType }>>;
